/**
 * Pure URL-state helpers for the Linear-style tasks page. Server pages
 * call `resolveTasksState(searchParams)` to derive the canonical state
 * (effective statuses, updated_since, etc.) before querying the DB,
 * then pass the same fields into the client-side `<TasksHeader>`.
 *
 * Kept in a `"use client"`-free file so it can be imported by server
 * components — the `<TasksHeader>` component itself lives in
 * `tasks-header.tsx` (client).
 */
import { STATUS_ORDER, type TaskStatus } from "../../types/projects";
import { TASK_STATUS_INTENT, type Intent } from "../../tokens";

export type TaskSection = "all" | "active" | "backlog";
export type TaskView = "list" | "board" | "review";
export type TaskRange = "7d" | "30d" | "90d" | "all" | "custom";

export const SECTION_PRESETS: Record<TaskSection, TaskStatus[]> = {
  all: ["backlog", "todo", "in_progress", "review", "done", "cancelled"],
  active: ["todo", "in_progress", "review"],
  backlog: ["backlog"],
};

/**
 * The status segments the integrated Issues header exposes as its primary
 * navigation — Open (an aggregate of the open buckets) · In progress · Needs
 * review · Done. Ordered active → done so the row reads as a pipeline. Kept in
 * this `"use client"`-free module so BOTH the server header producer (which
 * resolves the active segment + counts) and the client controls (`TasksHeader`,
 * `IssuesStatusPills`) share one vocabulary — the segments never fork.
 */
export interface StatusSegment {
  key: string;
  label: string;
  statuses: TaskStatus[];
  intent: Intent;
}

export const STATUS_SEGMENTS: readonly StatusSegment[] = [
  { key: "open", label: "Open", statuses: ["in_progress", "review", "todo", "backlog"], intent: "neutral" },
  { key: "in_progress", label: "In progress", statuses: ["in_progress"], intent: TASK_STATUS_INTENT.in_progress },
  { key: "review", label: "Needs review", statuses: ["review"], intent: TASK_STATUS_INTENT.review },
  { key: "done", label: "Done", statuses: ["done"], intent: TASK_STATUS_INTENT.done },
];

/** The open buckets — the set every Issues surface lands on. Mirrors the
 *  `STATUS_SEGMENTS` "open" segment and the mode mount's injected default. */
export const OPEN_STATUSES: TaskStatus[] = ["in_progress", "review", "todo", "backlog"];

/** Which segment a resolved status set corresponds to (an exact set match), or
 *  null when the user holds a custom filter that matches no single segment. */
export function activeStatusSegment(statuses: TaskStatus[]): string | null {
  const cur = new Set(statuses);
  const seg = STATUS_SEGMENTS.find(
    (s) => s.statuses.length === cur.size && s.statuses.every((x) => cur.has(x)),
  );
  return seg ? seg.key : null;
}

export interface ResolvedTasksState {
  section: TaskSection;
  view: TaskView;
  range: TaskRange;
  from?: string;
  to?: string;
  statuses: TaskStatus[];
  assignee?: string;
  projectSlug?: string;
  showEmpty: boolean;
  /** Final statuses[] to query against — section preset OR `?status=` override. */
  effectiveStatuses: TaskStatus[];
  /** ISO timestamp to pass as `updated_since`, or undefined for all-time. */
  updatedSince?: string;
}

const SECTIONS: ReadonlySet<TaskSection> = new Set([
  "all",
  "active",
  "backlog",
]);
const VIEWS: ReadonlySet<TaskView> = new Set(["list", "board", "review"]);
const RANGES: ReadonlySet<TaskRange> = new Set([
  "7d",
  "30d",
  "90d",
  "all",
  "custom",
]);
const STATUSES_SET: ReadonlySet<TaskStatus> = new Set(STATUS_ORDER);

function pickEnum<T extends string>(
  raw: string | undefined,
  allowed: ReadonlySet<T>,
  fallback: T,
): T {
  return raw && allowed.has(raw as T) ? (raw as T) : fallback;
}

function parseStatusList(raw: string | undefined): TaskStatus[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s): s is TaskStatus => STATUSES_SET.has(s as TaskStatus));
}

const DAY_MS = 24 * 60 * 60 * 1000;

export function resolveTasksState(searchParams: {
  section?: string;
  view?: string;
  range?: string;
  from?: string;
  to?: string;
  status?: string;
  assignee?: string;
  project?: string;
  empty?: string;
}): ResolvedTasksState {
  const section = pickEnum(searchParams.section, SECTIONS, "active");
  const view = pickEnum(searchParams.view, VIEWS, "list");
  const range = pickEnum(searchParams.range, RANGES, "7d");
  const statuses = parseStatusList(searchParams.status);
  const effectiveStatuses =
    statuses.length > 0 ? statuses : SECTION_PRESETS[section];

  let updatedSince: string | undefined;
  let from = searchParams.from;
  let to = searchParams.to;
  if (range === "7d") {
    updatedSince = new Date(Date.now() - 7 * DAY_MS).toISOString();
  } else if (range === "30d") {
    updatedSince = new Date(Date.now() - 30 * DAY_MS).toISOString();
  } else if (range === "90d") {
    updatedSince = new Date(Date.now() - 90 * DAY_MS).toISOString();
  } else if (range === "custom" && from) {
    updatedSince = `${from}T00:00:00`;
  } else {
    from = undefined;
    to = undefined;
  }

  return {
    section,
    view,
    range,
    from,
    to,
    statuses,
    assignee: searchParams.assignee?.trim() || undefined,
    projectSlug: searchParams.project?.trim() || undefined,
    showEmpty: searchParams.empty === "1",
    effectiveStatuses,
    updatedSince,
  };
}
